import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

const ROLE_OPTIONS = [
  { value: 'educator', label: 'Educator' },
  { value: 'student', label: 'Student' },
  { value: 'admin', label: 'Administrator' },
];

const SUBJECTS_BY_PHASE = {
  foundation: ['HL', 'FAL', 'Maths', 'Life Skills'],
  intermediate: ['HL', 'FAL', 'Maths', 'Life Skills', 'NST', 'SS'],
  senior: ['HL', 'FAL', 'Maths', 'NS', 'SS (History + Geography)', 'Tech', 'EMS', 'LO'],
};

const allSubjects = Array.from(
  new Set([
    ...SUBJECTS_BY_PHASE.foundation,
    ...SUBJECTS_BY_PHASE.intermediate,
    ...SUBJECTS_BY_PHASE.senior,
  ])
);

const getPhaseForGrade = (grade) => {
  const num = Number(grade);
  if (!Number.isFinite(num)) return null;
  if (num >= 1 && num <= 3) return 'foundation';
  if (num >= 4 && num <= 6) return 'intermediate';
  if (num >= 7 && num <= 9) return 'senior';
  return null;
};

const defaultValues = {
  name: '',
  surname: '',
  email: '',
  password: '',
  role: 'educator',
  grade: '',
  subjects: [],
  phone: '',
};

const UserFormDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({ defaultValues });

  const role = watch('role');
  const grade = watch('grade');
  const editing = Boolean(initialValues?._id);
  const phase = getPhaseForGrade(grade);
  const subjectOptions = role === 'student' || role === 'educator'
    ? phase
      ? SUBJECTS_BY_PHASE[phase]
      : []
    : [];
  const subjectsDisabled = (role === 'student' || role === 'educator') && !phase;

  useEffect(() => {
    if (open) {
      reset({
        ...defaultValues,
        ...initialValues,
        subjects: Array.isArray(initialValues?.subjects) ? initialValues.subjects : [],
        grade: initialValues?.grade || '',
      });
    }
  }, [initialValues, open, reset]);

  const submitHandler = (values) => {
    const phaseKey = getPhaseForGrade(values.grade);
    const allowedSubjects = (values.role === 'student' || values.role === 'educator') && phaseKey
      ? SUBJECTS_BY_PHASE[phaseKey]
      : values.role === 'educator'
        ? []
        : [];
    const payload = {
      ...values,
      subjects: Array.isArray(values.subjects)
        ? values.subjects.filter((subject) => allowedSubjects.includes(subject))
        : [],
      grade: values.role === 'student' ? Number(values.grade) || undefined : undefined,
    };
    if (editing && !payload.password) {
      delete payload.password;
    }
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
      <form onSubmit={handleSubmit(submitHandler)} noValidate>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                fullWidth
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                {...register('name', { required: 'First name is required' })}
              />
              <TextField
                label="Surname"
                fullWidth
                error={Boolean(errors.surname)}
                helperText={errors.surname?.message}
                {...register('surname', { required: 'Surname is required' })}
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              fullWidth
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {!editing && (
              <TextField
                label="Password"
                type="password"
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Must be at least 6 characters' },
                })}
              />
            )}
            {editing && (
              <TextField
                label="Password (leave blank to keep current)"
                type="password"
                fullWidth
                {...register('password')}
              />
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Role"
                fullWidth
                {...register('role', { required: 'Role is required' })}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Phone"
                fullWidth
                {...register('phone')}
              />
            </Stack>
            {(role === 'student' || role === 'educator') && (
              <TextField
                label={role === 'educator' ? 'Grade (for subject mapping)' : 'Grade'}
                type="number"
                inputProps={{ min: 1, max: 12 }}
                fullWidth
                error={Boolean(errors.grade)}
                helperText={errors.grade?.message}
                {...register('grade', {
                  required: role === 'student' ? 'Grade is required for students' : false,
                  valueAsNumber: true,
                  min: { value: 1, message: 'Minimum grade is 1' },
                  max: { value: 12, message: 'Maximum grade is 12' },
                })}
              />
            )}
            {(role === 'educator' || role === 'student') && (
              <Controller
                name="subjects"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    label="Subjects"
                    fullWidth
                    disabled={subjectsDisabled}
                    helperText={subjectsDisabled ? 'Select a grade to load subjects' : undefined}
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) =>
                        selected && selected.length ? selected.join(', ') : 'Select subjects',
                    }}
                  >
                    {subjectOptions.map((subject) => (
                      <MenuItem key={subject} value={subject}>
                        {subject}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserFormDialog;
